import ReactDOM from 'react-dom';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { authFetch, authPost } from '../../utils/api';
import { TC, TBadge } from './trainTheme';
import { ACSLogModal } from './ACSLogModal';

export function ACSSection({ missionKey, missions, onFocusChange, clearFocusRef, viewingStudent }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [standards, setStandards] = useState([]);
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCats, setExpandedCats] = useState({});
  const isCertOpen = (key) => expandedCats[key] === true;
  React.useEffect(() => {
    const focused = Object.keys(expandedCats).some(k => k.startsWith('cert-') && expandedCats[k]);
    onFocusChange && onFocusChange(focused);
  }, [expandedCats]);
  const isCatOpen  = (key) => expandedCats[key] === true;
  const [logModal, setLogModal] = useState(null);
  const [taskDetail, setTaskDetail] = useState(null);
  const [pilotFamilies, setPilotFamilies] = useState([]);
  const [schoolNames, setSchoolNames] = useState([]);
  const [filter, setFilter] = useState('due');
  const [explainerExp, setExplainerExp] = useState(false);

  const loadACS = useCallback(() => {
    if (!token) return;
    setLoading(true);
    authFetch(`/api/train/acs${viewingStudent ? `?as_student=${viewingStudent.user_id}` : ''}`, token)
      .then(d => {
        setStandards(d.standards || []);
        setCerts(d.certs || []);
        setPilotFamilies(d.pilot_families || []);
        setSchoolNames(d.school_names || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token, viewingStudent]);

  useEffect(loadACS, [loadACS]);

  const handleLog = async (acs_id, notes, date, opts = {}) => {
    try {
      await authPost(`/api/train/acs/${acs_id}/complete`, {
        notes, completed_at: date,
        scored_approach_id: opts.scored_approach_id || null,
        verifier_type: opts.verifier_type || null,
        verifier_id: opts.verifier_id || null,
      }, token);
      loadACS();
    } catch(e) { alert(e.message); }
    setLogModal(null);
  };

  const handleUnlog = async (acs_id) => {
    try {
      await authPost(`/api/train/acs/${acs_id}/uncomplete`, {}, token);
      loadACS();
    } catch(e) {}
  };

  const isDue = (s) => {
    if (!s.last_completed) return true;
    const days = (Date.now() - new Date(s.last_completed)) / 86400000;
    return days >= (s.frequency_days || 90);
  };

  const formatDate = (iso) => {
    if (!iso) return null;
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  };

  const filtered = standards.filter(s => {
    if (filter === 'due') return isDue(s);
    if (filter === 'done') return !isDue(s);
    return true;
  });

  const grouped = {};
  filtered.forEach(s => {
    const cl = s.cert_level || 'Other';
    const cat = s.category || 'General';
    if (!grouped[cl]) grouped[cl] = {};
    if (!grouped[cl][cat]) grouped[cl][cat] = [];
    grouped[cl][cat].push(s);
  });

  const certOrder = ['Private', 'Instrument', 'Commercial', 'CFI', 'Mission Specific', 'AC Type', 'Other'];
  const sortedCertLevels = Object.keys(grouped).sort((a, b) => {
    const ai = certOrder.indexOf(a), bi = certOrder.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  const totalDue = standards.filter(isDue).length;
  const totalDone = standards.filter(s => !isDue(s)).length;
  const focusedCert = sortedCertLevels.find(cl => expandedCats[`cert-${cl}`] === true) || null;
  const clearFocus = React.useCallback(() => {
    setExpandedCats(p => {
      const next = { ...p };
      Object.keys(next).forEach(k => { if (k.startsWith('cert-')) next[k] = false; });
      return next;
    });
  }, []);
  React.useEffect(() => { if (clearFocusRef) clearFocusRef.current = clearFocus; }, [clearFocus]);

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: TC.muted }}>Loading standards...</div>;

  if (standards.length === 0) return (
    <div style={{ padding: '40px 24px', textAlign: 'center' }}>
      <div style={{ fontSize: 40, marginBottom: 14 }}>📋</div>
      <div style={{ fontSize: 17, fontWeight: 700, color: TC.text, marginBottom: 8 }}>No ACS Standards Found</div>
      <div style={{ fontSize: 15, color: TC.muted, lineHeight: 1.6, maxWidth: 280, margin: '0 auto' }}>
        {certs.length === 0
          ? 'Add your certificates in your profile to see relevant ACS standards.'
          : `No active standards found for your certs: ${certs.join(', ')}. Standards can be managed in the console.`}
      </div>
    </div>
  );

  return (
    <div style={{ padding: '12px 16px 32px' }}>
      {/* Explainer */}
      <div onClick={focusedCert ? clearFocus : () => setExplainerExp(v => !v)}
        style={{ padding: '12px 14px', borderRadius: 8,
        background: TC.accentDim, border: `1px solid ${TC.accent}22`, marginBottom: 14, cursor: 'pointer',
        opacity: focusedCert ? 0.35 : 1, transition: 'opacity 0.25s ease' }}>
        {explainerExp ? (
          <span style={{ fontSize: 17, color: TC.muted, lineHeight: 1.65 }}>
            {'ACS/Standards track your currency against FAA Airman Certification Standards as well as other relevant standards (such as manufacturer or org). Tap any task to log it \u2014 '}
            <strong style={{ color: TC.text }}>the clock resets on each log</strong>
            {'. Tasks go red when overdue based on their recurrency interval. '}
            <span style={{ color: TC.accent, fontWeight: 700 }}>Less ▴</span>
          </span>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 17, color: TC.muted, flex: 1,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              ACS/Standards track your currency against FAA Airman Certification Standards and other relevant standards.
            </span>
            <span style={{ fontSize: 15, color: TC.accent, fontWeight: 700, flexShrink: 0 }}>More ▾</span>
          </div>
        )}
      </div>

      {/* Summary row */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14,
        opacity: focusedCert ? 0.35 : 1, transition: 'opacity 0.25s ease',
        pointerEvents: 'auto', cursor: focusedCert ? 'pointer' : 'auto' }}
        onClick={focusedCert ? clearFocus : undefined}>
        {[
          { key: 'due',  label: `Due ${totalDue}`,           color: TC.red,    bg: TC.redDim   },
          { key: 'done', label: `Current ${totalDone}`,      color: TC.green,  bg: TC.greenDim },
        ].map(chip => (
          <button key={chip.key} onClick={() => setFilter(chip.key)}
            style={{ flex: 1, padding: '7px 4px', borderRadius: 8, border: `1px solid ${filter === chip.key ? chip.color : TC.border}`,
              background: filter === chip.key ? chip.bg : TC.card, color: filter === chip.key ? chip.color : TC.muted,
              fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            {chip.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: '24px 0', color: TC.muted, fontSize: 15 }}>
          No {filter === 'due' ? 'overdue' : 'current'} standards.
        </div>
      )}

      {sortedCertLevels.map(certLevel => {
        const certKey = `cert-${certLevel}`;
        const certExpanded = isCertOpen(certKey);
        const certStandards = Object.values(grouped[certLevel]).flat();
        const certDue = certStandards.filter(isDue).length;

        return (
          <div key={certLevel} style={{ marginBottom: 10,
            opacity: focusedCert && focusedCert !== certLevel ? 0.35 : 1,
            transition: 'opacity 0.25s ease', pointerEvents: 'auto',
            cursor: focusedCert && focusedCert !== certLevel ? 'pointer' : 'auto' }}
          onClick={focusedCert && focusedCert !== certLevel ? clearFocus : undefined}>
            {/* Cert level header */}
            {(() => {
              const isACType = certLevel === 'AC Type';
              const isMsnSpec = certLevel === 'Mission Specific';
              const isSchool  = schoolNames.includes(certLevel);
              const hdrColor = isACType ? '#22d3ee' : isMsnSpec ? '#a78bfa' : isSchool ? '#f87171' : TC.accent;
              const hdrBg    = isACType ? 'rgba(34,211,238,0.1)' : isMsnSpec ? 'rgba(167,139,250,0.1)' : isSchool ? 'rgba(248,113,113,0.08)' : TC.accentDim;
              const hdrBorder= isACType ? '#22d3ee33' : isMsnSpec ? '#a78bfa33' : isSchool ? '#f8717133' : `${TC.accent}33`;
              return (
            <button onClick={() => setExpandedCats(p => ({ ...p, [certKey]: !certExpanded }))}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '9px 12px', borderRadius: certExpanded ? '10px 10px 0 0' : 10,
                background: hdrBg, border: `1px solid ${hdrBorder}`,
                cursor: 'pointer', textAlign: 'left' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 17, fontWeight: 800, color: hdrColor, letterSpacing: '0.03em' }}>
                  {isACType ? '\u2708 AC Type Specific' : isSchool ? (
                    <span style={{ display:'inline-flex', alignItems:'center', gap:7 }}>
                      <span>🏫 {certLevel}</span>
                      {certStandards.every(s => s.scoring_exempt) && (
                        <span style={{ fontSize:11, fontWeight:600, padding:'2px 7px', borderRadius:10,
                          background:'rgba(248,113,113,0.12)', color:'#f87171', border:'1px solid #f8717133' }}>
                          info only
                        </span>
                      )}
                    </span>
                  ) : certLevel}
                </span>
                {isACType && pilotFamilies.length > 0 && pilotFamilies.map(f => (
                  <span key={f} style={{ fontSize: 12, fontWeight: 700, padding: '1px 7px', borderRadius: 4,
                    background: 'rgba(34,211,238,0.15)', color: '#22d3ee', border: '1px solid #22d3ee33' }}>{f}</span>
                ))}
                <span style={{ fontSize: 13, color: TC.muted }}>{certStandards.length} tasks</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {certDue > 0 && (
                  <span style={{ fontSize: 13, fontWeight: 700, color: TC.red, background: TC.redDim,
                    padding: '2px 7px', borderRadius: 20 }}>{certDue} due</span>
                )}
                <span style={{ fontSize: 14, color: TC.dim }}>{certExpanded ? '\u25b2' : '\u25bc'}</span>
              </div>
            </button>
              );})()}

            {certExpanded && Object.entries(grouped[certLevel]).map(([category, items]) => {
              const catKey = `${certLevel}::${category}`;
              const catExpanded = isCatOpen(catKey);
              const catDue = items.filter(isDue).length;

              return (
                <div key={category} style={{ border: `1px solid ${TC.border}`, borderTop: 'none' }}>
                  <button onClick={() => setExpandedCats(p => ({ ...p, [catKey]: !catExpanded }))}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 14px', background: TC.bg, border: 'none', borderBottom: catExpanded ? `1px solid ${TC.border}` : 'none',
                      cursor: 'pointer', textAlign: 'left' }}>
                    <span style={{ fontSize: 16, fontWeight: 600, color: TC.text }}>{category}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {catDue > 0 && (
                        <span style={{ fontSize: 12, fontWeight: 700, color: TC.amber }}>{catDue} due</span>
                      )}
                      <span style={{ fontSize: 13, color: TC.dim }}>{catExpanded ? '\u25b2' : '\u25bc'}</span>
                    </div>
                  </button>

                  {catExpanded && items.map((s, idx) => {
                    const due = isDue(s);
                    const isLast = idx === items.length - 1;
                    const freq = s.frequency_days || 90;
                    const daysSince = s.last_completed ? (Date.now() - new Date(s.last_completed)) / 86400000 : null;
                    const pct = daysSince !== null ? Math.min(daysSince / freq, 1) : null;
                    const barColor = pct === null ? TC.border : pct >= 1 ? TC.red : pct >= 0.75 ? TC.amber : TC.green;
                    return (
                      <div key={s.id} onClick={() => setTaskDetail({ s, due, pct, barColor, daysSince, freq })}
                        style={{ padding: '10px 14px', borderBottom: isLast ? 'none' : `1px solid ${TC.border}`,
                          background: TC.card, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                          background: due ? TC.red : TC.green, opacity: 0.85 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ fontSize: 15, fontWeight: 600, color: TC.text,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            display: 'block' }}>{s.task}</span>
                          {s.scoring_exempt && (
                            <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10,
                              background: 'rgba(248,113,113,0.12)', color: '#f87171', border: '1px solid #f8717133' }}>
                              info only
                            </span>
                          )}
                        </div>
                        {pct !== null && (
                          <div style={{ width: 32, height: 4, borderRadius: 2, background: TC.border, overflow: 'hidden', flexShrink: 0 }}>
                            <div style={{ height: '100%', width: `${pct * 100}%`, background: barColor, borderRadius: 2 }} />
                          </div>
                        )}
                        {s.verified ? (
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#10b981', background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 6, padding: '2px 6px', flexShrink: 0 }}>✓ Verified</span>
                        ) : s.verified_by_id ? (
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#f59e0b', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 6, padding: '2px 6px', flexShrink: 0 }}>⏳ Pending</span>
                        ) : null}
                        <span style={{ fontSize: 14, color: TC.dim, flexShrink: 0 }}>›</span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}

      {taskDetail && ReactDOM.createPortal(
        <div onClick={() => setTaskDetail(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999,
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: TC.card, borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 600,
              maxHeight: '85vh', overflowY: 'auto', padding: '20px 20px 48px', WebkitOverflowScrolling: 'touch' }}>
            <div style={{ width: 40, height: 4, borderRadius: 2, background: TC.border, margin: '0 auto 20px' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                background: taskDetail.due ? TC.red : TC.green }} />
              <span style={{ fontSize: 14, fontWeight: 700, padding: '3px 10px', borderRadius: 6,
                background: taskDetail.due ? TC.redDim : 'rgba(34,197,94,0.12)',
                color: taskDetail.due ? TC.red : TC.green }}>
                {taskDetail.due ? 'Due' : 'Current'}
              </span>
              {taskDetail.s.acs_ref && <TBadge color={TC.dim} bg={TC.bg}>{taskDetail.s.acs_ref}</TBadge>}
              <span onClick={() => setTaskDetail(null)}
                style={{ marginLeft: 'auto', fontSize: 22, cursor: 'pointer', color: TC.muted }}>✕</span>
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: TC.text, lineHeight: 1.3, marginBottom: 12 }}>
              {taskDetail.s.task}
            </div>
            {taskDetail.s.description && (
              <div style={{ fontSize: 16, color: TC.muted, lineHeight: 1.7, marginBottom: 16,
                background: TC.bg, borderRadius: 10, padding: 14 }}>
                {taskDetail.s.description}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
              <TBadge color={taskDetail.s.item_type === 'knowledge' ? '#8b5cf6' : TC.accent}
                bg={taskDetail.s.item_type === 'knowledge' ? 'rgba(139,92,246,0.12)' : TC.accentDim}>
                {taskDetail.s.item_type === 'knowledge' ? 'knowledge' : 'maneuver'}
              </TBadge>
              <TBadge color={TC.dim} bg={TC.bg}>
                every {taskDetail.s.frequency_days >= 365 ? `${taskDetail.s.frequency_days/365}yr` : `${taskDetail.s.frequency_days}d`}
              </TBadge>
              {taskDetail.s.mission_relevant && <TBadge color={TC.accent} bg={TC.accentDim}>mission</TBadge>}
              {taskDetail.s.scoring_exempt && (
                <TBadge color="#f87171" bg="rgba(248,113,113,0.12)">info only — not scored</TBadge>
              )}
            </div>
            {taskDetail.pct !== null ? (
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, color: TC.muted }}>Logged {formatDate(taskDetail.s.last_completed)}</span>
                    {taskDetail.s.verified ? (
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#10b981', background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 6, padding: '2px 8px' }}>✓ Verified</span>
                    ) : taskDetail.s.verified_by_id ? (
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#f59e0b', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 6, padding: '2px 8px' }}>⏳ Pending Verification</span>
                    ) : (
                      <span style={{ fontSize: 12, color: TC.dim }}>Not verified</span>
                    )}
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 700, color: taskDetail.barColor }}>
                    {taskDetail.pct >= 1
                      ? `${Math.ceil(taskDetail.daysSince - taskDetail.freq)}d overdue`
                      : `${Math.max(0, Math.ceil(taskDetail.freq - taskDetail.daysSince))}d left`}
                  </span>
                </div>
                <div style={{ height: 6, borderRadius: 3, background: TC.border, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${taskDetail.pct * 100}%`,
                    background: taskDetail.barColor, borderRadius: 3 }} />
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 15, color: TC.muted, fontStyle: 'italic', marginBottom: 20 }}>
                Never logged — log it below to start the clock.
              </div>
            )}
            {taskDetail.s.verified && (
              <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.2)', marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#10b981', marginBottom: 6 }}>✓ Verification Details</div>
                {taskDetail.s.verified_by_name && (
                  <div style={{ fontSize: 13, color: TC.muted }}>Verified by: <span style={{ color: TC.text, fontWeight: 600 }}>{taskDetail.s.verified_by_name}{taskDetail.s.verified_by_role ? ' (' + taskDetail.s.verified_by_role + ')' : ''}</span></div>
                )}
                {taskDetail.s.verified_at && (
                  <div style={{ fontSize: 13, color: TC.muted, marginTop: 2 }}>Date: <span style={{ color: TC.text }}>{new Date(taskDetail.s.verified_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span></div>
                )}
                {taskDetail.s.verified_by_type && (
                  <div style={{ fontSize: 13, color: TC.muted, marginTop: 2 }}>Source: <span style={{ color: TC.text }}>{taskDetail.s.verified_by_type === 'school' ? 'Flight School' : 'CFI'}</span></div>
                )}
              </div>
            )}
            {taskDetail.s.verified_by_id && !taskDetail.s.verified && (
              <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.2)', marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#f59e0b', marginBottom: 4 }}>⏳ Pending Verification</div>
                <div style={{ fontSize: 13, color: TC.muted }}>Submitted to: <span style={{ color: TC.text }}>{taskDetail.s.verified_by_type === 'school' ? 'Flight School' : 'CFI'}</span></div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { setLogModal(taskDetail.s); setTaskDetail(null); }}
                style={{ flex: 1, padding: 13, borderRadius: 10, border: `1px solid ${TC.accent}`,
                  background: TC.accentDim, color: TC.accent, fontSize: 17, fontWeight: 800, cursor: 'pointer' }}>
                + Log
              </button>
              {taskDetail.s.last_completed && (
                <button onClick={() => { handleUnlog(taskDetail.s.id); setTaskDetail(null); }}
                  style={{ padding: '13px 20px', borderRadius: 10, border: `1px solid ${TC.border}`,
                    background: TC.bg, color: TC.muted, fontSize: 17, cursor: 'pointer' }}>
                  Undo
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {logModal && (
        <ACSLogModal item={logModal} onClose={() => setLogModal(null)} onSave={handleLog} token={token} navigate={navigate} />
      )}
    </div>
  );
}

export default ACSSection;
